import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAlerts } from '../../shared/api/api';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import Pagination from '../../shared/components/Pagination';
import FilterDropdown from '../../shared/components/FilterDropdown';
import usePagination from '../../shared/hooks/usePagination';
import useUpdateEffect from '../../shared/hooks/useUpdateEffect';
import useResizableColumns, { ICON_COL_MIN_WIDTH } from '../../shared/hooks/useResizableColumns';
import '../../shared/styles/resizableTable.css';
import './AlertsPage.css';

const ALERTS_COLUMNS = [
  { key: 'severity', label: 'Severity', width: 110 },
  { key: 'state', label: 'State', width: 100 },
  { key: 'alarm_name', label: 'Alarm', width: 220 },
  { key: 'service', label: 'Service', width: 140 },
  { key: 'environment', label: 'Env', width: 100 },
  { key: 'source', label: 'Source', width: 120 },
  { key: 'teams', label: 'Teams', width: 160 },
  { key: 'received_at', label: 'Received', width: 150 },
  { key: 'action', label: '', width: 90, minWidth: ICON_COL_MIN_WIDTH },
];

function AlertsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Restored from navigation state when returning via "Back to Alerts" — see
  // the View button below and AlertDetailPage's back button, which relays
  // this whole object rather than picking fields out of it. Anything absent
  // (a fresh visit to /alerts, not a return trip) falls back to the same
  // defaults this page always had.
  const restore = location.state || {};
  const [severityFilter, setSeverityFilter] = useState(restore.severityFilter || []);
  const [stateFilter, setStateFilter] = useState(restore.stateFilter || []);
  const [serviceFilter, setServiceFilter] = useState(restore.serviceFilter || []);
  const [environmentFilter, setEnvironmentFilter] = useState(restore.environmentFilter || []);
  const [sourceFilter, setSourceFilter] = useState(restore.sourceFilter || []);
  const [searchQuery, setSearchQuery] = useState(restore.searchQuery || '');
  const [sortField, setSortField] = useState(restore.sortField || 'received_at');
  const [sortDirection, setSortDirection] = useState(restore.sortDirection || 'desc');

  const { colWidths, tableWidth, handleResizeStart } = useResizableColumns(ALERTS_COLUMNS);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await getAlerts();
      const data = response.data;
      const list = Array.isArray(data) ? data : data?.alerts || data?.content || [];
      setAlerts(list);
    } catch {
      setError('Failed to load alerts.');
    } finally {
      setLoading(false);
    }
  };

  const severityOptions = useMemo(
    () => [...new Set(alerts.map((a) => a.severity).filter(Boolean))].sort(),
    [alerts]
  );

  const stateOptions = useMemo(
    () => [...new Set(alerts.map((a) => a.state).filter(Boolean))].sort(),
    [alerts]
  );

  const serviceOptions = useMemo(
    () => [...new Set(alerts.map((a) => a.service).filter(Boolean))].sort(),
    [alerts]
  );

  const environmentOptions = useMemo(
    () => [...new Set(alerts.map((a) => a.environment).filter(Boolean))].sort(),
    [alerts]
  );

  const sourceOptions = useMemo(
    () => [...new Set(alerts.map((a) => a.source).filter(Boolean))].sort(),
    [alerts]
  );

  const hasActiveFilters =
    severityFilter.length > 0 ||
    stateFilter.length > 0 ||
    serviceFilter.length > 0 ||
    environmentFilter.length > 0 ||
    sourceFilter.length > 0 ||
    searchQuery.trim() !== '';

  const clearAllFilters = useCallback(() => {
    setSeverityFilter([]);
    setStateFilter([]);
    setServiceFilter([]);
    setEnvironmentFilter([]);
    setSourceFilter([]);
    setSearchQuery('');
  }, []);

  const getSeverityClass = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return 'critical';
      case 'HIGH': return 'high';
      case 'MEDIUM': return 'medium';
      case 'LOW': return 'low';
      default: return 'info';
    }
  };

  const getStateClass = (state) => {
    switch (state?.toUpperCase()) {
      case 'ALARM': return 'alarm';
      case 'OK': return 'ok';
      case 'INSUFFICIENT_DATA': return 'insufficient';
      default: return 'unknown';
    }
  };

  const filteredAlerts = alerts
    .filter((a) => severityFilter.length === 0 || severityFilter.includes(a.severity))
    .filter((a) => stateFilter.length === 0 || stateFilter.includes(a.state))
    .filter((a) => serviceFilter.length === 0 || serviceFilter.includes(a.service))
    .filter((a) => environmentFilter.length === 0 || environmentFilter.includes(a.environment))
    .filter((a) => sourceFilter.length === 0 || sourceFilter.includes(a.source))
    .filter((a) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        a.alarm_name?.toLowerCase().includes(q) ||
        a.alarm_description?.toLowerCase().includes(q) ||
        a.service?.toLowerCase().includes(q) ||
        a.metric_name?.toLowerCase().includes(q) ||
        a.alert_id?.toLowerCase().includes(q) ||
        a.state_reason?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'received_at' || sortField === 'state_change_time') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const {
    currentPage,
    pageSize,
    totalPages,
    paginated,
    setCurrentPage,
    setPageSize,
    resetPage,
  } = usePagination(filteredAlerts, 15, restore.page);

  // useUpdateEffect (not useEffect) so this doesn't fire on mount — that would
  // immediately overwrite the page restored above, from returning via "Back
  // to Alerts" to something other than page 1.
  useUpdateEffect(() => {
    resetPage();
  }, [severityFilter, stateFilter, serviceFilter, environmentFilter, sourceFilter, searchQuery]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field) => {
    const icon = sortField === field ? (sortDirection === 'asc' ? '\u25B2' : '\u25BC') : '';
    return <span className="alert-sort-icon">{icon}</span>;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '\u2014';
    return new Date(ts).toLocaleString();
  };

  // Stats
  const criticalCount = alerts.filter((a) => a.severity === 'CRITICAL').length;
  const highCount = alerts.filter((a) => a.severity === 'HIGH').length;
  const alarmCount = alerts.filter((a) => a.state === 'ALARM').length;

  return (
    <div className="page-container">
      {loading && <LoadingSpinner message="Loading alerts..." />}
      <div className="alerts-header">
        <h1 className="page-title">Alerts</h1>
        <div className="alerts-stats">
          <div className="alerts-stat">
            <span className="alerts-stat-value">{alerts.length}</span>
            <span className="alerts-stat-label">Total</span>
          </div>
          <div className="alerts-stat alerts-stat--critical">
            <span className="alerts-stat-value">{criticalCount}</span>
            <span className="alerts-stat-label">Critical</span>
          </div>
          <div className="alerts-stat alerts-stat--high">
            <span className="alerts-stat-value">{highCount}</span>
            <span className="alerts-stat-label">High</span>
          </div>
          <div className="alerts-stat alerts-stat--alarm">
            <span className="alerts-stat-value">{alarmCount}</span>
            <span className="alerts-stat-label">In Alarm</span>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Filter bar */}
      <div className="alerts-filter-bar">
        <div className="alerts-filter-row">
          <div className="alerts-search-field">
            <svg className="alerts-search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input
              type="text"
              className="alerts-search-input"
              placeholder="Search alerts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="alerts-search-clear" onClick={() => setSearchQuery('')}>{'\u2715'}</button>
            )}
          </div>

          <FilterDropdown label="Severity" options={severityOptions} selected={severityFilter} onChange={setSeverityFilter} />
          <FilterDropdown label="State" options={stateOptions} selected={stateFilter} onChange={setStateFilter} />
          <FilterDropdown label="Service" options={serviceOptions} selected={serviceFilter} onChange={setServiceFilter} />
          {environmentOptions.length > 0 && (
            <FilterDropdown label="Environment" options={environmentOptions} selected={environmentFilter} onChange={setEnvironmentFilter} />
          )}
          {sourceOptions.length > 0 && (
            <FilterDropdown label="Source" options={sourceOptions} selected={sourceFilter} onChange={setSourceFilter} />
          )}
        </div>

        {hasActiveFilters && (
          <div className="alerts-filter-row">
            <button className="alerts-clear-all" onClick={clearAllFilters}>Clear filters</button>
          </div>
        )}
      </div>

      {/* Alerts table */}
      <div className="card">
        <div className="alerts-table-toolbar">
          <span className="alerts-table-count">{filteredAlerts.length} of {alerts.length} alerts</span>
          <button className="btn btn-secondary alerts-refresh-btn" onClick={fetchAlerts}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            Refresh
          </button>
        </div>
        {paginated.length === 0 ? (
          <p className="empty-state">No alerts found.</p>
        ) : (
          <div className="alerts-table-wrapper">
            <table className="alerts-table resizable-table" style={{ width: tableWidth }}>
              <colgroup>
                {ALERTS_COLUMNS.map((c) => (
                  <col key={c.key} style={{ width: `${colWidths[c.key]}px` }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {ALERTS_COLUMNS.map((c) => {
                    const sortable = c.key !== 'teams' && c.key !== 'action';
                    return (
                      <th
                        key={c.key}
                        className={`resizable-th${sortable ? ' alerts-th-sortable' : ''}`}
                        onClick={sortable ? () => handleSort(c.key) : undefined}
                      >
                        {c.label}
                        {sortable && getSortIndicator(c.key)}
                        <span className="col-resize-handle" onMouseDown={handleResizeStart(c.key)} onClick={(e) => e.stopPropagation()} />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {paginated.map((a) => (
                  <tr key={a.alert_id}>
                    <td>
                      <span className={`alerts-severity alerts-severity--${getSeverityClass(a.severity)}`}>
                        {a.severity || '\u2014'}
                      </span>
                    </td>
                    <td>
                      <span className={`alerts-state alerts-state--${getStateClass(a.state)}`}>
                        {a.state || '\u2014'}
                      </span>
                    </td>
                    <td className="alerts-alarm-name">{a.alarm_name || '\u2014'}</td>
                    <td className="alerts-service">{a.service || '\u2014'}</td>
                    <td><span className="alerts-env-badge">{a.environment || '\u2014'}</span></td>
                    <td className="alerts-source">{a.source || '\u2014'}</td>
                    <td className="alerts-teams">
                      {a.teams?.map((t) => (
                        <span key={t} className="alerts-team-badge">{t}</span>
                      )) || '\u2014'}
                    </td>
                    <td className="alerts-timestamp">{formatTimestamp(a.received_at)}</td>
                    <td>
                      <button
                        className="btn-link"
                        onClick={() => navigate(`/alerts/${a.alert_id}`, {
                          state: {
                            page: currentPage,
                            severityFilter, stateFilter, serviceFilter, environmentFilter, sourceFilter,
                            searchQuery, sortField, sortDirection,
                          },
                        })}
                      >
                        View {'\u2192'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={filteredAlerts.length}
        displayedItems={paginated.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}

export default AlertsPage;
